import { paths } from '@/constants/paths'
import { getBasicPageMetaData, Seo } from '@/layout/Seo'
import { DefaultLayout } from '@/layouts/DefaultLayout'
import { NextPageWithLayout } from '@/layouts/types'
import { GuildGeogatedBenefitsPrompt } from '@/organisms/GuildGeogatedBenefitsPrompt'
import { getGuildStatus } from '@/services/GuildUserService'
import { useStickyAttribution } from '@/utils/analytics/StickyAttribution'
import { getServerSidePropsErrorHandler } from '@/utils/nextUtils/nextErrorHandlers'
import { useTranslate } from '@/utils/translate/translate-client'
import { loadTranslations } from '@/utils/translate/translate-server'
import { getEmptyGuildStatus, GuildStatus } from '@/utils/users/usersServer'
import { GuildGiftView } from '@/views/GuildGiftView/GuildGiftView'

interface GuildGiftPageProps {
  locale: string
  region: string | null
  isLoggedIn: boolean
  guildStatus: GuildStatus
}

export const GiftGuildPage: NextPageWithLayout<GuildGiftPageProps> = (props) => {
  const { t } = useTranslate('guild')
  useStickyAttribution('guild-attribution')

  return (
    <div className="bg-core-gray-950">
      <Seo
        {...getBasicPageMetaData({
          cloudinaryImagePath: 'v1756153598/angel-studios/seo/angel-opengraph-5',
          cloudinaryTransformation: 'b_rgb:000000',
          description: t(
            'guildGiftMetaDescription',
            'Give the gift of an Angel Guild membership. Purchase a gift so they can vote on shows, enjoy early access and member discounts, and help amplify light.',
          ),
          locale: props.locale,
          path: `${paths.guild.gift.index}`,
          title: 'Angel Guild Membership | Gift the Guild',
        })}
      />
      <GuildGiftView isLoggedIn={props.isLoggedIn} guildStatus={props.guildStatus} />
      <GuildGeogatedBenefitsPrompt />
    </div>
  )
}

GiftGuildPage.getLayout = (page) => {
  return (
    <DefaultLayout isDarkMode={true} hideTicketsNavBanner>
      {page}
    </DefaultLayout>
  )
}

export const getServerSideProps = getServerSidePropsErrorHandler(
  async ({ isLoggedIn, federationClient, locale, region }) => {
    const [guildStatus, translations] = await Promise.all([
      getGuildStatus(federationClient),
      loadTranslations(locale, ['common', 'guild', 'home', 'watch', 'join-the-guild']),
    ])

    return {
      props: {
        locale,
        region,
        isLoggedIn,
        guildStatus: guildStatus ?? getEmptyGuildStatus(),
        isDarkMode: true,
        ...translations,
      },
    }
  },
)

export default GiftGuildPage
